import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import {
  parseUrlRedirect,
  resolveRedirect,
  useNav,
  type RedirectProp,
} from '@hero/web-hooks';
import { ArrowLeftIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';

export interface PageBackProps {
  /**
   * Where the back button should redirect.
   *
   * - `false` / `undefined`: hide the button (default — pages don't grow
   *   a back button by accident; consumers opt in).
   * - `true`: show the button, redirect to the carrier-dashboard "More"
   *   screen (`settings`). The most common case.
   * - `'route_name'`: redirect to the given typed route.
   * - `{ name, params? }`: redirect to a route with path / search params.
   *
   * If the URL also carries `?back=…`, that wins. This lets a host page
   * (e.g. a deep link from the customer drawer) override the default
   * "back to More" behaviour without having to know about every consumer.
   */
  redirect?: RedirectProp;
  /**
   * Optional label rendered next to the back chevron. When omitted falls
   * back to the shared "Back" string from `c-main:navigation.back`.
   */
  title?: string;
  /** Extra Tailwind classes for the outer button row (margin / padding). */
  className?: string;
}

/**
 * Reusable "back" button for sub-pages.
 *
 * Hidden by default — only renders when {@link PageBackProps.redirect}
 * is set or the URL carries a `?back=…` search param. Resolves the
 * destination through the typed `useNav()` system so there are no string
 * literals at the call site, and the destination is statically checked
 * against the route registry.
 *
 * Visual: leading `ArrowLeftIcon` (RTL-aware via `rtl:rotate-180`),
 * `text-start`, hover ring. Designed to sit at the top of a page above
 * the toolbar and match the design language of the carrier-dashboard
 * More sub-pages (Users, Pricing, Contacts, Places, Profile).
 */
export function PageBack({ redirect, title, className }: PageBackProps) {
  const { t } = useTranslation(['c-main']);
  const { navigate } = useNav();
  const [searchParams] = useSearchParams();

  // URL ?back=… overrides the prop so deep links can opt sub-pages into
  // showing a back button without the page itself knowing.
  const target = useMemo(() => {
    const fromUrl = parseUrlRedirect(searchParams);
    return resolveRedirect(fromUrl ?? redirect);
  }, [searchParams, redirect]);

  if (!target) return null;

  const label = title ?? t('c-main:navigation.back', { defaultValue: 'Back' });

  return (
    <div className={cn('mb-4 flex items-center', className)}>
      <button
        type="button"
        onClick={() => navigate(target.name as never, target.params as never)}
        className={cn(
          'group inline-flex items-center gap-2 rounded-full px-3 py-1.5',
          'text-sm font-medium text-gray-600',
          'hover:bg-gray-100 hover:text-gray-900',
          'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300',
        )}
        aria-label={label}>
        <ArrowLeftIcon className="size-4 rtl:rotate-180" />
        <span>{label}</span>
      </button>
    </div>
  );
}